import {
  YesNoButtonsBase,
  type YesNoButtonsBaseProps,
  type YesNoOptionBase,
} from "@core/ui";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { SelectableChip } from "../SelectChips/SelectableChip";

export type YesNoOption = YesNoOptionBase;

// Omit renderButton since we provide our own styled implementation
export type Props = Omit<YesNoButtonsBaseProps, "renderButton"> & {
  /** Display variant - "buttons" (default) or "chips" (SelectChips style) */
  variant?: "buttons" | "chips";
};

/**
 * Styled yes/no buttons component.
 * Pure UI component - works with or without react-hook-form.
 *
 * @example
 * <YesNoButtons
 *   name="agree"
 *   options={[
 *     { value: 1, label: "Yes" },
 *     { value: 0, label: "No" },
 *   ]}
 *   value={answer}
 *   onChange={setAnswer}
 * />
 */
export function YesNoButtons({
  name,
  options,
  value = [],
  onChange,
  onBlur,
  error,
  disabled = false,
  id,
  className,
  variant = "buttons",
  ...props
}: Props) {
  const isChips = variant === "chips";

  return (
    <YesNoButtonsBase
      name={name}
      options={options}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      error={error}
      disabled={disabled}
      id={id}
      className={twMerge(
        "flex flex-row gap-2",
        isChips && "flex-wrap gap-x-2 gap-y-3",
        className
      )}
      renderButton={({ option, isSelected, isDisabled, onClick }) =>
        isChips ? (
          <SelectableChip
            key={option.value}
            title={option.label}
            isChecked={isSelected}
            onChange={() => onClick()}
            isDisabled={isDisabled}
            testId={`yesno-${name}-${option.label.toLowerCase()}`}
            // One answer only, inside the group's radiogroup.
            role="radio"
          />
        ) : (
          <Button
            key={option.value}
            className="w-full"
            variant={isSelected ? "primary" : "secondary"}
            type="button"
            onClick={onClick}
            disabled={isDisabled}
            data-testid={`yesno-${name}-${option.label.toLowerCase()}`}
            aria-label={option.label}
            aria-pressed={isSelected}
          >
            {option.label}
          </Button>
        )
      }
      {...props}
    />
  );
}

export default YesNoButtons;

